import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { PathName, getRouteByKey } from 'utility/routes';
import Persist, { StorageKind } from 'lib/persist';
import { RootState, useAppDispatch } from 'lib/centralStore';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { setFiscalCode, setSecretQuestion } from 'features/resetPassword/resetPasswordSlice';
import { useLazyCheckFiscalCodeQuery, useLazySignUpCheckFiscalCodeQuery } from 'features/api/userSlice';

import { BannerInformation } from 'components/bannerInformation';
import { PreviousNextContainer } from '../previousNextContainer';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import { RichText } from 'components/richText';
import { TextInput } from 'components/inputs/textInput';
import fiscalCodeStyle from './FiscalCode.module.scss';
import { gtag } from 'utility/functions';
import passwordResetStyle from '../../style/password.module.scss';
import { setFiscalCodeSignup } from 'features/signup/signup';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const localStorage = Persist(StorageKind.local);

export type FiscalCodeProps = {
  labels: {
    title: string;
    subtitle?: string;
    labelInput: string;
    labelPlaceholder: string;
    continueButton: string;
  };
  next: Dispatch<SetStateAction<true>>;
  isCheckRegistration?: boolean;
  errorCf?: string;
};

export const FiscalCode: React.FC<FiscalCodeProps> = ({ labels, next, isCheckRegistration, errorCf }) => {
  const [trigger, { isFetching }] = useLazyCheckFiscalCodeQuery();
  const [signupCheck, { isFetching: isFetchingSignup }] = useLazySignUpCheckFiscalCodeQuery();
  const [errorMessageVisible, setErrorMessageVisible] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const state = useSelector((state: RootState) => state.resetPassword);
  const dispatch = useAppDispatch();
  const { asPath } = useRouter();
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);
  const schema = z.object({
    fiscalCode: z
      .string()
      .regex(
        /^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i
      ),
  });
  type SchemaValidator = z.infer<typeof schema>;

  const { handleSubmit, control, formState, setError } = useForm<SchemaValidator>({
    resolver: zodResolver(schema),
    defaultValues: {
      fiscalCode: state.fiscalCode,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const cf = useWatch({ control, name: 'fiscalCode' });

  useEffect(() => {
    if (isCheckRegistration) {
      setErrorMessageVisible(false);
    }
  }, [cf, isCheckRegistration]);

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);
      if (step !== 'fiscal-code') {
        gtag(
          {
            section: 'RegistrationStepFiscalCode',
            detail: 'RegistrationStepFiscalCode',
            event: 'RegistrationStepFiscalCode',
          },
          true
        );
        localStorage.setItem(REGISTRATION_STEP_KEY, 'fiscal-code');
      }
    }
  }, [registrationUrl, asPath]);

  const createError = (errorDataResponse: any) => {
    if (errorDataResponse.esito !== 1024) {
      if (errorDataResponse.esito === -28) {
        setErrorMsg(errorCf);
      } else {
        if (errorDataResponse.descrizione) {
          setErrorMsg(errorDataResponse.descrizione);
        } else {
          setErrorMsg(undefined);
        }
      }
    }
  };

  const onSubmit: SubmitHandler<SchemaValidator> = async ({ fiscalCode }) => {
    if (isCheckRegistration) {
      try {
        await signupCheck(fiscalCode).unwrap();

        dispatch(setFiscalCodeSignup(fiscalCode));
        next(true);
      } catch (error) {
        if (error.data?.response) {
          createError(error.data?.response);
          setErrorMessageVisible(true);
        } else {
          setError('fiscalCode', { types: { custom: error.data?.descrizione } });
        }
      }
    } else {
      dispatch(setFiscalCode({ fiscalCode }));
      try {
        const responseCheckFiscalCode = await trigger({ fiscalCode }).unwrap();
        dispatch(
          setSecretQuestion({
            secretQuestion: responseCheckFiscalCode.cod_domanda_segreta,
          })
        );
        next(true);
      } catch (error) {
        setError('fiscalCode', { types: { custom: 'Invalid fiscal code' } });
      }
    }
  };

  return isCheckRegistration ? (
    <div className={fiscalCodeStyle.wrapper}>
      {errorMessageVisible && errorCf && (
        <BannerInformation
          text={errorMsg ? errorMsg : ''}
          type="error"
          visible={!!errorMsg}
          className={fiscalCodeStyle.banner}
        />
      )}
      <div className={fiscalCodeStyle.container}>
        <h2 className={fiscalCodeStyle.title}>{labels.title}</h2>
        {labels.subtitle && <RichText containerClassName={fiscalCodeStyle.subTitle}>{labels.subtitle}</RichText>}
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            control={control}
            name="fiscalCode"
            hasMarginBottom
            label={labels.labelInput}
            placeholder={labels.labelPlaceholder}
            className={fiscalCodeStyle.fiscalCode}
            onChange={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />

          <PreviousNextContainer isNextDisabled={!formState.isValid} isLoading={isFetchingSignup} />
        </form>
      </div>
    </div>
  ) : (
    <div className={passwordResetStyle.container}>
      <h2 className={passwordResetStyle.title}>{labels.title}</h2>
      {labels.subtitle && <RichText containerClassName={passwordResetStyle.subTitle}>{labels.subtitle}</RichText>}
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          control={control}
          name="fiscalCode"
          hasMarginBottom
          label={labels.labelInput}
          placeholder={labels.labelPlaceholder}
          className={fiscalCodeStyle.fiscalCode}
          onChange={(e) => {
            e.target.value = e.target.value.toUpperCase();
          }}
        />
        <PreviousNextContainer isNextDisabled={!formState.isValid} isLoading={isFetching} />
      </form>
    </div>
  );
};

export default FiscalCode;
